import type { EmailBodyPart } from '../api/types';

/*
    Which attachments can be looked at, and how.

    Kept apart from the overlay that draws them because these are decisions, not drawing: they
    are the same on every screen, they have to agree with the server, and they are the part worth
    testing without a browser in the room.
*/

export type PreviewKind = 'image' | 'pdf' | 'document' | 'text';

/*
    What the server will hand over for display, listed rather than matched by prefix.

    `image/*` was the rule here once, and it was wrong in a way nobody would notice until it
    mattered: the server serves seven image types for display and no others, so an iPhone photo
    (image/heic) or an SVG was offered as previewable and then arrived as a picture the browser
    cannot draw. An empty frame is a worse answer than the download button.
*/
const NATIVE_IMAGE = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/x-icon',
    'image/vnd.microsoft.icon',
]);

/** What the converter is trusted to open. Kept in step with convertible in the server's preview.go. */
const CONVERTIBLE = new Set([
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'text/csv',
]);

/** Plain text large enough to be a log file is a download, not a read. */
export const TEXT_PREVIEW_LIMIT = 1 << 20;

/**
 * How this part can be shown, or null if it cannot be.
 *
 * `documents` is what the server said about itself at sign-in: with no converter beside it, an
 * office file is a download and the interface never suggests otherwise.
 */
export function previewKind(a: EmailBodyPart, documents: boolean): PreviewKind | null {
    if (!a.partId) return null;
    const type = (a.type ?? '').toLowerCase();
    if (NATIVE_IMAGE.has(type)) return 'image';
    if (type === 'application/pdf') return 'pdf';
    if (type === 'text/plain') return a.size <= TEXT_PREVIEW_LIMIT ? 'text' : null;
    if (documents && CONVERTIBLE.has(type)) return 'document';
    return null;
}

export function canPreview(a: EmailBodyPart, documents: boolean): boolean {
    return previewKind(a, documents) !== null;
}

/*
    Bytes into letters.

    A text attachment carries no reliable statement of its encoding — the charset parameter is
    often missing and often wrong. UTF-8 is tried strictly, so it fails rather than producing
    replacement characters, and what is left in this mail is overwhelmingly Windows-1254: a
    decade of Turkish correspondence written before UTF-8 won. Getting it wrong is the difference
    between "Şubat" and "Åubat".
*/
export function decodeText(bytes: Uint8Array): string {
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        return new TextDecoder('windows-1254').decode(bytes);
    }
}
